import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Service } from '../../../../../packages/contracts/src/index.js';
import { ActionMenu } from '../../components/ui/action-menu';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { Input, Label, Select } from '../../components/ui/field';
import { PaginationControls, useLocalPagination } from '../../components/ui/pagination';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatCurrency, formatDate } from '../../shared/format';
import { labelForServiceCategory } from '../../shared/i18n';

type BillingUnit = 'hour' | 'night' | 'entrance' | 'fixed';

const today = () => new Date().toISOString().slice(0, 10);

const billingUnitLabels: Record<BillingUnit, string> = {
  hour: 'Por hora',
  night: 'Por noche',
  entrance: 'Por entrada',
  fixed: 'Fijo',
};

interface PricePanelConfig {
  readonly title: string;
  readonly body: string;
  readonly category: Service['category'];
  readonly unit: BillingUnit;
  readonly defaultServiceName: string;
  readonly amountLabel: string;
}

const panels: PricePanelConfig[] = [
  {
    title: 'Guardería por hora',
    body: 'Define el precio que se usará para calcular guarderías según sus horas registradas.',
    category: 'daycare',
    unit: 'hour',
    defaultServiceName: 'Guardería por hora',
    amountLabel: 'Precio por hora MXN',
  },
  {
    title: 'Guarderia por entradas',
    body: 'Define el precio que se usara para paquetes de guarderia con entradas prepagadas.',
    category: 'daycare',
    unit: 'entrance',
    defaultServiceName: 'Guarderia por entrada',
    amountLabel: 'Precio por entrada MXN',
  },
  {
    title: 'Hotel',
    body: 'Define el precio que se usará para cobrar hospedaje por noche.',
    category: 'boarding',
    unit: 'night',
    defaultServiceName: 'Hotel por noche',
    amountLabel: 'Precio por noche MXN',
  },
];

function latestFirst<T extends { effectiveFrom: string; createdAt?: string }>(items: T[]) {
  return [...items].sort((first, second) => {
    const byDate = second.effectiveFrom.localeCompare(first.effectiveFrom);
    if (byDate !== 0) return byDate;
    return (second.createdAt ?? '').localeCompare(first.createdAt ?? '');
  });
}

function suggestedUnit(category?: string): BillingUnit {
  if (category === 'daycare') return 'hour';
  if (category === 'boarding') return 'night';
  return 'fixed';
}

export function PricesPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: () => api.services.list({ limit: 100 }) });
  const [serviceId, setServiceId] = useState(() => searchParams.get('serviceId') ?? '');
  const selectedService = servicesQuery.data?.items.find((service) => service.id === serviceId);
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [billingUnit, setBillingUnit] = useState<BillingUnit>('fixed');
  const [active, setActive] = useState(true);
  const pricesQuery = useQuery({
    queryKey: ['service-prices', serviceId],
    queryFn: () => api.services.prices(serviceId, { limit: 100 }),
    enabled: Boolean(serviceId),
  });

  const sortedPrices = useMemo(() => latestFirst(pricesQuery.data?.items ?? []), [pricesQuery.data?.items]);

  const createPrice = useMutation({
    mutationFn: () =>
      api.services.createPrice(serviceId, {
        amountCents: Math.max(0, Math.round(Number(amount || '0') * 100)),
        currency: 'MXN',
        billingUnit,
        effectiveFrom,
        active,
      }),
    onSuccess: async () => {
      setAmount('');
      await queryClient.invalidateQueries({ queryKey: ['service-prices', serviceId] });
    },
  });

  const changeService = (id: string) => {
    const service = servicesQuery.data?.items.find((item) => item.id === id);
    setServiceId(id);
    setBillingUnit(suggestedUnit(service?.category));
  };

  if (servicesQuery.isLoading) return <LoadingState label="Cargando precios" />;
  if (servicesQuery.isError) return <ErrorState message={servicesQuery.error.message} />;

  const services = servicesQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Precios</h1>
        <p className="mt-1 text-sm text-slate-500">Define tarifas para servicios del catálogo.</p>
      </div>

      <section className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
        <Card>
          <CardHeader><h2 className="text-sm font-semibold">Nueva tarifa</h2></CardHeader>
          <CardContent>
            <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); createPrice.mutate(); }}>
              <div className="grid gap-1.5">
                <Label htmlFor="serviceId">Servicio</Label>
                <Select id="serviceId" value={serviceId} required onChange={(event) => changeService(event.target.value)}>
                  <option value="">Selecciona un servicio</option>
                  {services.map((service) => <option key={service.id} value={service.id}>{service.name} · {labelForServiceCategory(service.category)}</option>)}
                </Select>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="amount">Monto MXN</Label>
                  <Input id="amount" type="number" min="0" step="1" required value={amount} onChange={(event) => setAmount(event.target.value)} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="billingUnit">Unidad</Label>
                  <Select id="billingUnit" value={billingUnit} onChange={(event) => setBillingUnit(event.target.value as BillingUnit)}>
                    <option value="hour">Por hora</option>
                    <option value="night">Por noche</option>
                    <option value="entrance">Por entrada</option>
                    <option value="fixed">Fijo</option>
                  </Select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="effectiveFrom">Vigente desde</Label>
                  <Input id="effectiveFrom" type="date" required value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} />
                </div>
                <label className="mt-7 flex h-9 items-center gap-2 text-sm font-medium text-[#4f5d49]">
                  <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
                  Activa
                </label>
              </div>
              {createPrice.isError ? <p className="text-sm text-rose-600">{createPrice.error.message}</p> : null}
              <Button type="submit" disabled={!selectedService || createPrice.isPending}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Agregar precio
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-sm font-semibold">{selectedService ? `Tarifas de ${selectedService.name}` : 'Tarifas'}</h2></CardHeader>
          <CardContent>
            {!serviceId ? <EmptyState title="Selecciona un servicio" body="El historial de precios aparece después de elegir un servicio." /> : null}
            {pricesQuery.isLoading ? <LoadingState label="Cargando tarifas" /> : null}
            {pricesQuery.isError ? <ErrorState message={pricesQuery.error.message} /> : null}
            {serviceId && sortedPrices.length === 0 && !pricesQuery.isLoading ? <EmptyState title="Sin tarifas" body="Agrega la primera tarifa para este servicio." /> : null}
            {sortedPrices.length > 0 ? <PriceTable serviceId={serviceId} prices={sortedPrices} onConfirm={setConfirm} /> : null}
          </CardContent>
        </Card>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function PresetPricePanel({ config, services }: { readonly config: PricePanelConfig; readonly services: Service[] }) {
  const queryClient = useQueryClient();
  const categoryServices = services.filter((service) => service.category === config.category);
  const [serviceId, setServiceId] = useState(categoryServices[0]?.id ?? '');
  const selectedServiceId = serviceId || categoryServices[0]?.id || '';
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today());

  const pricesQuery = useQuery({
    queryKey: ['service-prices', selectedServiceId],
    queryFn: () => api.services.prices(selectedServiceId, { limit: 100 }),
    enabled: Boolean(selectedServiceId),
  });

  const sortedPrices = useMemo(() => latestFirst(pricesQuery.data?.items ?? []), [pricesQuery.data?.items]);

  const createServiceAndPrice = useMutation({
    mutationFn: async () => {
      const service = selectedServiceId
        ? services.find((item) => item.id === selectedServiceId)
        : await api.services.create({
            name: config.defaultServiceName,
            category: config.category,
            active: true,
          });

      if (!service) throw new Error('Selecciona un servicio.');

      return api.services.createPrice(service.id, {
        amountCents: Math.max(0, Math.round(Number(amount || '0') * 100)),
        currency: 'MXN',
        billingUnit: config.unit,
        effectiveFrom,
        active: true,
      });
    },
    onSuccess: async () => {
      setAmount('');
      await queryClient.invalidateQueries({ queryKey: ['services'] });
      await queryClient.invalidateQueries({ queryKey: ['service-prices'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-sm font-semibold">{config.title}</h2>
          <p className="mt-1 text-sm text-slate-500">{config.body}</p>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); createServiceAndPrice.mutate(); }}>
          {categoryServices.length > 0 ? (
            <div className="grid gap-1.5">
              <Label htmlFor={`${config.category}-service`}>Servicio</Label>
              <Select id={`${config.category}-service`} value={selectedServiceId} onChange={(event) => setServiceId(event.target.value)}>
                {categoryServices.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </Select>
            </div>
          ) : (
            <p className="rounded-md border border-[#d6ded0] bg-[#f8faf6] p-3 text-sm text-[#4f5d49]">Se creará el servicio “{config.defaultServiceName}” automáticamente al guardar.</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor={`${config.category}-amount`}>{config.amountLabel}</Label>
              <Input id={`${config.category}-amount`} type="number" min="0" step="1" required value={amount} onChange={(event) => setAmount(event.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${config.category}-effectiveFrom`}>Vigente desde</Label>
              <Input id={`${config.category}-effectiveFrom`} type="date" required value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} />
            </div>
          </div>
          {createServiceAndPrice.isError ? <p className="text-sm text-rose-600">{createServiceAndPrice.error.message}</p> : null}
          <Button type="submit" disabled={createServiceAndPrice.isPending}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Guardar precio
          </Button>
        </form>

        {pricesQuery.isLoading ? <LoadingState label="Cargando tarifas" /> : null}
        {pricesQuery.isError ? <ErrorState message={pricesQuery.error.message} /> : null}
        {selectedServiceId && sortedPrices.length > 0 ? <PriceTable serviceId={selectedServiceId} prices={sortedPrices} /> : null}
      </CardContent>
    </Card>
  );
}

function PriceTable({ serviceId, prices, onConfirm }: { readonly serviceId: string; readonly prices: Array<{ id: string; amountCents: number; billingUnit: BillingUnit; effectiveFrom: string; active: boolean }>; readonly onConfirm?: (state: ConfirmState) => void }) {
  const queryClient = useQueryClient();
  const pricesPage = useLocalPagination(prices);
  const deletePrice = useMutation({
    mutationFn: (priceId: string) => api.services.removePrice(serviceId, priceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['service-prices', serviceId] });
      await queryClient.invalidateQueries({ queryKey: ['service-prices'] });
    },
  });

  const confirmDelete = (priceId: string) => onConfirm?.({
    title: 'Eliminar tarifa',
    body: '¿Eliminar esta tarifa? Esta acción sólo está permitida para administradores.',
    confirmLabel: 'Eliminar',
    onConfirm: () => deletePrice.mutate(priceId),
  });

  return (
    <div className="space-y-3 overflow-x-auto">
      <Table>
        <thead>
          <tr>
            <Th>Monto</Th>
            <Th>Unidad</Th>
            <Th>Vigente desde</Th>
            <Th>Estado</Th>
            <Th>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {pricesPage.pageItems.map((price) => (
            <tr key={price.id}>
              <Td className="font-medium text-slate-900">{formatCurrency(price.amountCents)}</Td>
              <Td>{billingUnitLabels[price.billingUnit] ?? 'Fijo'}</Td>
              <Td>{formatDate(price.effectiveFrom)}</Td>
              <Td>{price.active ? 'Activa' : 'Inactiva'}</Td>
              <Td>
                <ActionMenu
                  groups={[
                    {
                      label: 'Riesgo',
                      tone: 'danger',
                      children: (
                        <button
                          type="button"
                          className="flex items-center gap-2 rounded-md px-2 py-2 text-left text-[#8a4a3a] hover:bg-[#fff8f8] disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={deletePrice.isPending}
                          title="Eliminar tarifa"
                          aria-label="Eliminar tarifa"
                          onClick={() => confirmDelete(price.id)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          Eliminar
                        </button>
                      ),
                    },
                  ]}
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <PaginationControls {...pricesPage} onPageChange={pricesPage.setPage} />
      {deletePrice.isError ? <p className="text-sm text-rose-600">{deletePrice.error.message}</p> : null}
    </div>
  );
}
